import { Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography } from "@mui/material";
import { BillingSubscriptionItem, UpcomingInvoice } from "../../types/billing";
import { projectedInvoiceRows } from "../../utils/billingFormat";
import { formatCurrency } from "../../utils/formatCurrency";

interface Props {
  upcomingInvoice: UpcomingInvoice;
  items: BillingSubscriptionItem[];
}

export default function ProjectedInvoiceTable({ upcomingInvoice, items }: Props) {
  const rows = projectedInvoiceRows(upcomingInvoice, items);
  if (rows.length === 0) {
    return <Typography variant="body2" color="text.secondary">No usage recorded this period yet.</Typography>;
  }

  const formatMoney = (cents: number) => formatCurrency(cents, upcomingInvoice.currency);
  const recoveredAmount = rows.some((r) => r.recoveredAmount);
  const quantityTotal = rows
    .filter((r) => r.recoveredAmount === recoveredAmount)
    .reduce((sum, r) => sum + (r.quantity ?? 0), 0);
  const formatQuantity = (quantity: number, recovered: boolean) =>
    (recovered ? formatCurrency(quantity, upcomingInvoice.currency, false) : quantity);
  const amountTotal = rows.reduce((sum, r) => sum + r.amount, 0);

  return (
    <Table
      size="small"
      sx={{
        tableLayout: "fixed",
        minWidth: 560,
        "& .MuiTableCell-root": { width: "25%" },
        "& .MuiTableBody-root .MuiTableCell-root, & .MuiTableFooter-root .MuiTableCell-root": { borderBottom: 0 },
      }}
    >
      <TableHead>
        <TableRow sx={{ "& .MuiTableCell-root": { fontWeight: 400, color: "text.secondary", borderBottom: 1, borderColor: "divider" } }}>
          <TableCell>Description</TableCell>
          <TableCell align="right">Price</TableCell>
          <TableCell align="right">{recoveredAmount ? "Amount recovered" : "Quantity"}</TableCell>
          <TableCell align="right">Amount</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, idx) => (
          <TableRow key={idx}>
            <TableCell>
              {row.description}
              {row.period && (
                <Typography variant="body2" color="text.secondary">{row.period}</Typography>
              )}
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
              {row.unitPrice ?? "-"}
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
              {row.quantity == null ? "-" : formatQuantity(row.quantity, row.recoveredAmount)}
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{formatMoney(row.amount)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        <TableRow
          sx={{
            "& .MuiTableCell-root": {
              fontSize: "0.875rem", fontWeight: 600, color: "text.primary", borderTop: 1, borderColor: "divider",
            },
          }}
        >
          <TableCell>Total</TableCell>
          <TableCell />
          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{formatQuantity(quantityTotal, recoveredAmount)}</TableCell>
          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{formatMoney(amountTotal)}</TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );
}
